import React from 'react';
import { useIntl } from '../../util/reactIntl';
import classNames from 'classnames';
import {
  ensureUser,
  ensureCurrentUser,
  userDisplayNameAsString,
  userAbbreviatedName,
} from '../../util/data';
import { isUserAuthorized } from '../../util/userHelpers';

import { ResponsiveImage, IconBannedUser, NamedLink } from '../../components/';

import css from './Avatar.module.css';

const AVATAR_SIZES = '40px';
const AVATAR_SIZES_MEDIUM = '60px';
const AVATAR_SIZES_LARGE = '96px';

const AVATAR_IMAGE_VARIANTS = [
  'square-xsmall',
  'square-xsmall2x',
  'square-small',
  'square-small2x',
  'default',
];

// ---------------- Helpers for flag severity ------------------

const resolveFlagColor = flagged => {
  if (!flagged) return null;

  const fromString = typeof flagged === 'string' ? flagged.toLowerCase().trim() : null;

  const fromObject =
    typeof flagged === 'object'
      ? String(flagged.color ?? flagged.severity ?? flagged.level ?? '')
          .toLowerCase()
          .trim()
      : null;

  const color = fromString || fromObject || null;
  return ['red', 'yellow', 'orange'].includes(color) ? color : null;
};

/** Colored flag only (no circle). Color comes from CSS via currentColor. */
const FlagIcon = ({ color }) => (
  <span
    className={classNames(css.flagOnly, {
      [css.flagRed]: color === 'red',
      [css.flagYellow]: color === 'yellow',
      [css.flagOrange]: color === 'orange',
    })}
    aria-label={`flagged: ${color}`}
    title={`Flagged: ${color}`}
  >
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path
        /* Flag filled with currentColor; thin white stroke for contrast */
        d="M6 2.5v19a1 1 0 0 1-2 0v-19a1 1 0 0 1 2 0Zm12.2 2.5c.7 0 1.3.6 1.3 1.3v7.1c0 .9-1 1.4-1.8.9l-3.2-1.9a1 1 0 0 0-1-.02l-4.2 2.3c-.8.43-1.8-.15-1.8-1.04V5.3c0-.72.59-1.3 1.3-1.3h9.4Z"
        fill="currentColor"
        stroke="white"
        strokeWidth="0.75"
        strokeLinejoin="round"
      />
    </svg>
  </span>
);

// -------------------------------------------------------------

export const Avatar = props => {
  const intl = useIntl();
  const {
    rootClassName,
    className,
    initialsClassName,
    user,
    renderSizes = AVATAR_SIZES,
    disableProfileLink,
    profileImageUrl,
    isTopBar,
  } = props;
  const classes = classNames(rootClassName || css.root, className);
  const imageUrl = profileImageUrl;
  const userIsCurrentUser = user && user.type === 'currentUser';
  const avatarUser = userIsCurrentUser ? ensureCurrentUser(user) : ensureUser(user);

  const isUnauthorizedUser = userIsCurrentUser && !isUserAuthorized(user);
  const variant = user?.attributes?.state;

  const isBannedUser = avatarUser.attributes.banned;
  const isDeletedUser = avatarUser.attributes.deleted;

  const defaultUserDisplayName = isBannedUser
    ? intl.formatMessage({ id: 'Avatar.bannedUserDisplayName' })
    : isDeletedUser
    ? intl.formatMessage({ id: 'Avatar.deletedUserDisplayName' })
    : '';

  const displayName = userDisplayNameAsString(avatarUser, defaultUserDisplayName);
  const abbreviatedName = userAbbreviatedName(avatarUser, '');

  const rootProps = { className: classes, title: displayName };

  const linkProps =
    isUnauthorizedUser && avatarUser.id
      ? { name: 'ProfilePageVariant', params: { id: avatarUser.id.uuid, variant } }
      : avatarUser.id
      ? { name: 'ProfilePage', params: { id: avatarUser.id.uuid } }
      : { name: 'ProfileBasePage' };

  const hasProfileImage = avatarUser.profileImage && avatarUser.profileImage.id;
  const profileLinkEnabled = !disableProfileLink || !imageUrl;

  // --- Flagged logic ---
  const flaggedRaw = user?.attributes?.profile?.publicData?.flagged;
  const flaggedColor = resolveFlagColor(flaggedRaw);

  if (isBannedUser || isDeletedUser) {
    return (
      <div {...rootProps}>
        <IconBannedUser className={css.bannedUserIcon} />
        {/* {flaggedColor ? <FlagIcon color={flaggedColor} /> : null} */}
      </div>
    );
  } else if ((hasProfileImage && profileLinkEnabled && !isTopBar) || imageUrl) {
    return (
      <NamedLink {...rootProps} {...linkProps}>
        <ResponsiveImage
          rootClassName={css.avatarImage}
          alt={displayName}
          image={avatarUser.profileImage}
          imageUrl={imageUrl}
          variants={AVATAR_IMAGE_VARIANTS}
          sizes={renderSizes}
        />
        {/* {flaggedColor ? <FlagIcon color={flaggedColor} /> : null} */}
      </NamedLink>
    );
  } else if (hasProfileImage || imageUrl) {
    return (
      <div {...rootProps}>
        <ResponsiveImage
          rootClassName={css.avatarImage}
          alt={displayName}
          imageUrl={imageUrl}
          image={avatarUser.profileImage}
          variants={AVATAR_IMAGE_VARIANTS}
          sizes={renderSizes}
        />
        {/* {flaggedColor ? <FlagIcon color={flaggedColor} /> : null} */}
      </div>
    );
  } else if (profileLinkEnabled && !isTopBar) {
    return (
      <NamedLink {...rootProps} {...linkProps}>
        <span className={initialsClassName || css.initials}>{abbreviatedName}</span>
        {/* {flaggedColor ? <FlagIcon color={flaggedColor} /> : null} */}
      </NamedLink>
    );
  } else {
    return (
      <div {...rootProps}>
        <span className={initialsClassName || css.initials}>{abbreviatedName}</span>
        {/* {flaggedColor ? <FlagIcon color={flaggedColor} /> : null} */}
      </div>
    );
  }
};

export default Avatar;

export const AvatarSmall = props => (
  <Avatar
    rootClassName={css.smallAvatar}
    initialsClassName={css.initialsSmall}
    renderSizes={AVATAR_SIZES_MEDIUM}
    {...props}
  />
);
AvatarSmall.displayName = 'AvatarSmall';

export const AvatarMedium = props => (
  <Avatar
    rootClassName={css.mediumAvatar}
    initialsClassName={css.initialsMedium}
    renderSizes={AVATAR_SIZES_MEDIUM}
    {...props}
  />
);
AvatarMedium.displayName = 'AvatarMedium';

export const AvatarLarge = props => (
  <Avatar
    rootClassName={css.largeAvatar}
    initialsClassName={css.initialsLarge}
    renderSizes={AVATAR_SIZES_LARGE}
    {...props}
  />
);
AvatarLarge.displayName = 'AvatarLarge';
